import { useRef, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { User, Building2 } from "lucide-react";
import DropdownLayout from "../../components/DropdownLayout";
import { fromJS } from "immutable";
import { search } from "../../api/ProjectApi";
import AddVariableIcon from "../../icons/svg/add_variable.svg?react";

const EMAIL_VARIABLES_FALLBACK = [
  "contact.email",
  "contact.name",
  "sale.title",
  "sale.status",
  "sale.assignee",
  "company.name",
  "company.domain",
];

const SUGGEST_DEBOUNCE_MS = 300;

type ContactAccountSuggestion = {
  id: string;
  label: string;
  email: string;
  type: string;
  iconUrl?: string;
};

type InputWithVariablePickerProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  label?: string;
  attributes?: any;
  /** When provided, enables contact/account suggestions from API */
  projectId?: string;
  /** Filter function to filter variables (e.g., only show email variables) */
  variableFilter?: (variable: string) => boolean;
  /** Validation function that returns error message if invalid, or empty string if valid */
  validate?: (value: string) => string;
  /** Enable auto-formatting with comma separators */
  autoFormat?: boolean;
  /** Extra variables (e.g. previous step output: previousStepFirstResult, previousStep.output.aiFieldResults) */
  extraVariables?: string[];
  /** Enable variable highlighting */
  highlightVariables?: boolean;
};

const InputWithVariablePicker = ({
  value = "",
  onChange,
  placeholder,
  readOnly = false,
  label,
  attributes = fromJS([]),
  projectId,
  variableFilter,
  validate,
  autoFormat = false,
  extraVariables,
  highlightVariables = false,
}: InputWithVariablePickerProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [suggestions, setSuggestions] = useState<ContactAccountSuggestion[]>([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [cursorPosition, setCursorPosition] = useState<number>(value.length);
  const [variablesOpen, setVariablesOpen] = useState(false);
  const [variableFilter2, setVariableFilter2] = useState("");

  // Compute validation error
  const validationError = validate ? validate(value) : "";

  const getVariables = () => {
    let vars: string[];
    if (attributes && attributes.size > 0) {
      const attrVars = attributes
        .map((item: any) => item.get("value"))
        .filter(Boolean)
        .toJS();
      // Merge fallback variables to ensure common ones (sale.title, etc.) are always available
      const merged = [...EMAIL_VARIABLES_FALLBACK, ...attrVars];
      vars = Array.from(new Set(merged));
    } else {
      vars = [...EMAIL_VARIABLES_FALLBACK];
    }
    if (extraVariables && extraVariables.length > 0) {
      vars = Array.from(new Set([...vars, ...extraVariables]));
    }
    // Apply filter if provided
    if (variableFilter) {
      vars = vars.filter(variableFilter);
    }
    return vars;
  };

  const variables = getVariables();

  const searchContactAccount = useCallback(
    async (query: string): Promise<ContactAccountSuggestion[]> => {
      if (!projectId || !query.trim()) return [];
      try {
        const searchFilters = {
          name: { operator: "regex" as const, values: [query.trim()] },
        };
        const data = await search({
          projectId,
          pageSize: 10,
          types: ["CONTACT", "COMPANY"],
          filters: searchFilters,
        });
        const content = fromJS(data?.pageResult?.content || []);
        return content
          .filter((item: any) => !!item.getIn(["data", "email", "value"]))
          .map((item: any) => ({
            id: item.get("id"),
            label:
              item.getIn(["data", "name", "value"]) || item.getIn(["data", "email", "value"]) || "",
            email: item.getIn(["data", "email", "value"]),
            type: item.get("type"),
            iconUrl: item.get("iconUrl") || undefined,
          }))
          .toJS();
      } catch (error) {
        console.error("Error searching contact/account:", error);
        return [];
      }
    },
    [projectId]
  );

  const performSuggest = useCallback(
    async (query: string) => {
      if (!projectId) return;
      setSuggestionsOpen(true);
      setSuggestionsLoading(true);
      try {
        const results = await searchContactAccount(query);
        setSuggestions(results);
        setSuggestionsOpen(results.length > 0);
      } catch {
        setSuggestions([]);
        setSuggestionsOpen(false);
      } finally {
        setSuggestionsLoading(false);
      }
    },
    [projectId, searchContactAccount]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!suggestionsOpen && !variablesOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setSuggestionsOpen(false);
        setVariablesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [suggestionsOpen, variablesOpen]);

  const autoResize = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    autoResize(e.target);
    const pos = e.target.selectionStart ?? newValue.length;
    setCursorPosition(pos);

    // Check if user is typing a variable (detected "{{")
    const beforeCursor = newValue.slice(0, pos);
    const lastOpenBrace = beforeCursor.lastIndexOf("{{");
    const lastCloseBrace = beforeCursor.lastIndexOf("}}");

    // If there's an unclosed "{{" before cursor, show variables dropdown
    if (lastOpenBrace > lastCloseBrace) {
      const filterText = beforeCursor.slice(lastOpenBrace + 2); // Text after "{{"
      setVariableFilter2(filterText.toLowerCase());
      setVariablesOpen(true);
      setSuggestionsOpen(false);
      return;
    } else {
      setVariablesOpen(false);
      setVariableFilter2("");
    }

    if (!projectId || readOnly) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query =
      newValue
        .slice(0, pos)
        .split(/[\s,;]+/)
        .pop() || "";
    if (query.length < 1) {
      setSuggestionsOpen(false);
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      performSuggest(query);
      debounceRef.current = null;
    }, SUGGEST_DEBOUNCE_MS);
  };

  const handleSuggestionSelect = (item: ContactAccountSuggestion) => {
    const input = inputRef.current;
    if (!input || readOnly) return;
    const start = input.selectionStart ?? cursorPosition;
    const end = input.selectionEnd ?? cursorPosition;
    const before = value.slice(0, start);
    const lastDelimiter = Math.max(
      before.lastIndexOf(" "),
      before.lastIndexOf(","),
      before.lastIndexOf(";"),
      -1
    );
    const replaceStart = lastDelimiter + 1;

    // Auto-format: add comma separator if there's existing content
    let prefix = "";
    if (autoFormat && replaceStart > 0) {
      const existingContent = value.slice(0, replaceStart).trim();
      if (existingContent && !existingContent.endsWith(",")) {
        prefix = ", ";
      }
    }

    const newValue =
      value.slice(0, replaceStart).trimEnd() + prefix + item.email + value.slice(end);
    onChange(newValue);
    setSuggestionsOpen(false);
    setSuggestions([]);
    setTimeout(() => {
      input.focus();
      const newPos =
        value.slice(0, replaceStart).trimEnd().length + prefix.length + item.email.length;
      input.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleVariableSelect = (variable: string) => {
    const variableText = `{{${variable}}}`;
    const input = inputRef.current;
    if (!input || readOnly) return;

    const cursorPos = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? value.length;

    // If variablesOpen is true, we triggered this by typing "{{"
    // So we need to replace from "{{" position to cursor
    if (variablesOpen) {
      const beforeCursor = value.slice(0, cursorPos);
      const lastOpenBrace = beforeCursor.lastIndexOf("{{");
      if (lastOpenBrace >= 0) {
        // Auto-format: add comma separator if there's existing content before "{{"
        let prefix = "";
        if (autoFormat && lastOpenBrace > 0) {
          const existingContent = value.slice(0, lastOpenBrace).trim();
          if (existingContent && !existingContent.endsWith(",")) {
            prefix = ", ";
          }
        }

        const beforeBrace = value.slice(0, lastOpenBrace).trimEnd();
        const newValue = beforeBrace + prefix + variableText + value.slice(cursorPos);
        onChange(newValue);
        setVariablesOpen(false);
        setVariableFilter2("");

        setTimeout(() => {
          input.focus();
          const newCursorPos = beforeBrace.length + prefix.length + variableText.length;
          input.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
        return;
      }
    }

    // Normal case: triggered by clicking "Add Variable" button
    const start = cursorPos;

    // Auto-format: add comma separator if there's existing content
    let prefix = "";
    if (autoFormat && start > 0) {
      const existingContent = value.slice(0, start).trim();
      if (existingContent && !existingContent.endsWith(",")) {
        prefix = ", ";
      }
    }

    const beforeContent = value.slice(0, start).trimEnd();
    const newValue = beforeContent + prefix + variableText + value.slice(end);
    onChange(newValue);

    setTimeout(() => {
      input.focus();
      const newCursorPos = beforeContent.length + prefix.length + variableText.length;
      input.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const activator = (
    <button
      type="button"
      className={`inline-flex items-center gap-1.5 text-xs font-medium transition-colors ${
        readOnly
          ? "cursor-not-allowed opacity-50 text-gray-400"
          : "text-gray-500 hover:text-gray-700"
      }`}
    >
      <AddVariableIcon className="w-[14px] h-[14px] shrink-0" />
      <span>{t("Insert variable")}</span>
    </button>
  );

  const showSuggestions = projectId && !readOnly && (suggestionsOpen || suggestionsLoading);

  return (
    <div ref={containerRef} className="flex flex-col gap-1.5 w-full relative">
      {label && <label className="text-sm font-medium text-gray-700">{label}</label>}
      <div className="relative w-full">
        <div className="relative flex-1 min-w-0">
          {/* Highlighted layer */}
          {highlightVariables && value && (
            <div className="absolute inset-0 px-3 py-2 pointer-events-none text-sm whitespace-pre-wrap wrap-break-word min-h-[42px] overflow-hidden">
              {value.split(/(\{\{[^}]+\}\})/g).map((part, i) => {
                const match = part.match(/^\{\{([^}]+)\}\}$/);
                if (match) {
                  return (
                    <span
                      key={i}
                      className="px-1 mx-0.5 bg-yellow-100 text-yellow-800 rounded font-mono text-xs"
                    >
                      {part}
                    </span>
                  );
                }
                return (
                  <span key={i} className="text-gray-900">
                    {part}
                  </span>
                );
              })}
            </div>
          )}
          <textarea
            ref={(node) => {
              (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
              if (node) autoResize(node);
            }}
            readOnly={readOnly}
            rows={1}
            className={`w-full px-3 py-2 text-sm rounded-lg border bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 resize-none overflow-hidden ${
              highlightVariables ? "relative bg-transparent" : ""
            } ${
              validationError
                ? "border-red-300 focus:ring-red-500/20 focus:border-red-500"
                : "border-gray-200 focus:ring-blue-500/20 focus:border-blue-500"
            }`}
            value={value}
            placeholder={placeholder}
            onChange={handleInputChange}
            onSelect={() => {
              const input = inputRef.current;
              if (input) setCursorPosition(input.selectionStart ?? value.length);
            }}
            onFocus={() => {
              const input = inputRef.current;
              if (input) setCursorPosition(input.selectionStart ?? value.length);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
              }
            }}
            style={highlightVariables ? { color: "transparent", caretColor: "black" } : undefined}
          />
        </div>
        {!readOnly && !highlightVariables && (
          <DropdownLayout
            activator={activator}
            fullWidth={false}
            positionClassName="left-0 origin-top-left"
          >
            <ul className="max-h-56 overflow-y-auto min-w-52 py-1 rounded-lg shadow-lg border border-gray-100 bg-white">
              {variables.map((variable: string, index: number) => (
                <li
                  key={`var_${variable}_${index}`}
                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-700 hover:text-blue-700"
                  onClick={() => handleVariableSelect(variable)}
                >
                  <code className="text-xs font-mono">{`{{${variable}}}`}</code>
                </li>
              ))}
            </ul>
          </DropdownLayout>
        )}
        {/* Insert variable button */}
        {!readOnly && highlightVariables && (
          <button
            type="button"
            className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
            onClick={() => setVariablesOpen(!variablesOpen)}
          >
            <AddVariableIcon className="w-[14px] h-[14px] shrink-0" />
            <span>{t("Insert variable")}</span>
          </button>
        )}
      </div>
      {validationError && <p className="text-xs text-red-600">{validationError}</p>}
      {showSuggestions && (
        <ul
          className="absolute left-0 right-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          style={{ minWidth: "200px" }}
        >
          {suggestionsLoading ? (
            <li className="px-3 py-2 text-sm text-gray-500">{t("Loading...")}</li>
          ) : (
            suggestions.map((item) => (
              <li
                key={`suggest_${item.id}_${item.type}`}
                className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-700 hover:text-blue-700"
                onClick={() => handleSuggestionSelect(item)}
              >
                {item.iconUrl ? (
                  <img
                    src={item.iconUrl}
                    alt=""
                    className="w-6 h-6 rounded-full object-cover shrink-0"
                  />
                ) : item.type === "CONTACT" ? (
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                ) : (
                  <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4 text-gray-600" />
                  </div>
                )}
                <span className="min-w-0 truncate mr-1">{item.label}</span>
                <span className="text-xs text-gray-500 truncate max-w-[140px]">{item.email}</span>
              </li>
            ))
          )}
        </ul>
      )}
      {variablesOpen && !readOnly && (
        <ul
          className="absolute left-0 right-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          style={{ minWidth: "200px" }}
        >
          {variables
            .filter((v: string) => v.toLowerCase().includes(variableFilter2))
            .map((variable: string, index: number) => (
              <li
                key={`var_inline_${variable}_${index}`}
                className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-700 hover:text-blue-700"
                onClick={() => handleVariableSelect(variable)}
              >
                <code className="text-xs font-mono">{`{{${variable}}}`}</code>
              </li>
            ))}
          {variables.filter((v: string) => v.toLowerCase().includes(variableFilter2)).length ===
            0 && <li className="px-3 py-2 text-sm text-gray-500">{t("No variables found")}</li>}
        </ul>
      )}
    </div>
  );
};

export default InputWithVariablePicker;
